/**
 * Network Status Hook
 *
 * Monitors browser online/offline status and provides network state.
 * Triggers toast notifications when connectivity changes.
 *
 * Features:
 * - Tracks navigator.onLine state
 * - Listens to online/offline browser events
 * - Shows "back online" toast when connectivity is restored
 * - Shows "offline" error banner when connection is lost
 *
 * Usage:
 * ```tsx
 * // In a component that should monitor network status
 * function NetworkStatusMonitor() {
 *   useNetworkStatus();
 *   return null;
 * }
 *
 * // Or get the status for conditional rendering
 * function MyComponent() {
 *   const { isOnline } = useNetworkStatus();
 *   return isOnline ? <App /> : <OfflineMessage />;
 * }
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from './useToast';

export interface NetworkStatus {
  /** Whether the browser reports online status */
  isOnline: boolean;
  /** Whether we've detected the network status (false on initial render) */
  initialized: boolean;
}

/**
 * Hook to monitor and respond to network connectivity changes
 *
 * @param showNotifications - Whether to show toast notifications on status change (default: true)
 * @returns Current network status
 */
export const useNetworkStatus = (showNotifications = true): NetworkStatus => {
  const toast = useToast();
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [initialized, setInitialized] = useState(false);

  // Track if we were previously offline to avoid showing "back online" on initial load
  const wasOfflineRef = useRef(false);

  const handleOnline = useCallback(() => {
    setIsOnline(true);

    // Only show "back online" if we were previously offline
    if (showNotifications && wasOfflineRef.current) {
      toast.backOnline();
    }
    wasOfflineRef.current = false;
  }, [showNotifications, toast]);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    wasOfflineRef.current = true;

    if (showNotifications) {
      toast.offline();
    }
  }, [showNotifications, toast]);

  useEffect(() => {
    // Set initial state
    const currentOnline =
      typeof navigator !== 'undefined' ? navigator.onLine : true;
    setIsOnline(currentOnline);
    setInitialized(true);

    // If we start offline, mark it so we show "back online" when connectivity returns
    if (!currentOnline) {
      wasOfflineRef.current = true;
    }

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return {
    isOnline,
    initialized,
  };
};

/**
 * Component to monitor network status and show notifications
 * Place this once in your app, typically inside App.tsx
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <Providers>
 *       <NetworkStatusMonitor />
 *       <Routes />
 *     </Providers>
 *   );
 * }
 * ```
 */
export const NetworkStatusMonitor: React.FC = () => {
  useNetworkStatus(true);
  return null;
};

export default useNetworkStatus;
